// BAR CLAIMED celebration screen — ports canon ClaimCardScreen.
// Full-bleed colored backdrop + Burst rays + claiming team + venue name +
// "they had N · you had M" detail. Tap CTA to head back to the battle board.

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { type BattleDto, battlesApi } from '../../../../../api'
import { Icon } from '../../../../../components/Icon'

export const Route = createFileRoute('/squad/battle/$id/claimed/$venueId')({
  component: BarClaimedRoute,
})

function colorVar(color: string): string {
  if (color === 'pink') return 'var(--pink)'
  if (color === 'lime') return 'var(--lime)'
  if (color === 'teal') return 'var(--teal)'
  if (color === 'orange') return 'var(--orange)'
  return color
}

function fgFor(color: string): string {
  return color === 'pink' || color === 'orange' ? 'var(--paper)' : 'var(--ink)'
}

const RAYS = 10

function BarClaimedRoute() {
  const { id, venueId } = Route.useParams()
  const navigate = useNavigate()
  const [battle, setBattle] = useState<BattleDto | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const res = await battlesApi.get(id)
        setBattle(res.battle)
      } catch {
        navigate({ to: '/squad/battle/$id', params: { id } })
      }
    })()
  }, [id, navigate])

  if (!battle) {
    return (
      <div
        className="stumble-screen no-head"
        data-testid="bar-claimed-loading"
        style={{ display: 'grid', placeItems: 'center' }}
      >
        <p className="body-sm muted">Loading…</p>
      </div>
    )
  }

  const bv = battle.venues.find((v) => v.venueId === venueId)
  const claimingSquad =
    bv?.claimedBy === battle.squadA.id
      ? battle.squadA
      : bv?.claimedBy === battle.squadB.id
        ? battle.squadB
        : null
  // If for some reason the claim hasn't reflected yet, assume the calling
  // squad won by drink count.
  const inferred =
    bv && !claimingSquad ? (bv.countA >= bv.countB ? battle.squadA : battle.squadB) : null
  const winner = claimingSquad ?? inferred ?? battle.squadA
  const loser = winner.id === battle.squadA.id ? battle.squadB : battle.squadA
  const winnerCount = winner.id === battle.squadA.id ? (bv?.countA ?? 0) : (bv?.countB ?? 0)
  const loserCount = winner.id === battle.squadA.id ? (bv?.countB ?? 0) : (bv?.countA ?? 0)

  const winnerColor = colorVar(winner.color)
  const fg = fgFor(winner.color)

  return (
    <div
      className="stumble-screen no-head"
      data-testid="bar-claimed-screen"
      style={{
        background: `linear-gradient(180deg, ${winnerColor} 0% 60%, ${winnerColor} 100%)`,
        color: fg,
      }}
    >
      <div
        style={{
          padding: '60px 22px 30px',
          display: 'grid',
          gridTemplateRows: 'auto 1fr auto',
          height: '100%',
          gap: 12,
          paddingBottom: 'max(30px, calc(var(--safe-bottom, env(safe-area-inset-bottom)) + 22px))',
        }}
      >
        <div className="row-between">
          <span className="sticker" style={{ background: 'var(--paper)', color: 'var(--ink)' }}>
            squad mode
          </span>
          <button
            type="button"
            className="icon-btn"
            onClick={() => navigate({ to: '/squad/battle/$id', params: { id } })}
            aria-label="Back to battle"
            data-testid="bar-claimed-close"
          >
            <Icon name="close" size={20} />
          </button>
        </div>

        <div
          style={{
            display: 'grid',
            alignContent: 'center',
            gap: 14,
            position: 'relative',
          }}
        >
          {/* Burst rays — built inline (matches canon Burst component). */}
          <div className="burst" aria-hidden>
            {Array.from({ length: RAYS }).map((_, i) => (
              <div
                key={`ray-${i}-${(360 / RAYS) * i}`}
                className="ray"
                // biome-ignore lint/suspicious/noExplicitAny: CSS custom property typing
                style={{ ['--a' as any]: `${(360 / RAYS) * i}deg` }}
              />
            ))}
          </div>

          <div className="row" style={{ justifyContent: 'center' }}>
            <span
              className="stamp-badge"
              style={{ background: 'var(--paper)', color: 'var(--ink)' }}
            >
              {winner.name.toUpperCase()} CLAIMED
            </span>
          </div>

          <h1
            className="h-xl"
            style={{
              textAlign: 'center',
              maxWidth: '12ch',
              color: fg,
              justifySelf: 'center',
              fontSize: 48,
              lineHeight: 0.92,
            }}
            data-testid="bar-claimed-venue"
          >
            {bv?.venueName ?? 'this bar'} is yours.
          </h1>

          <div className="card" style={{ background: 'var(--paper)', color: 'var(--ink)' }}>
            <div className="row-between">
              <div>
                <div className="eyebrow">final count here</div>
                <div
                  className="display"
                  style={{ fontSize: 48, lineHeight: 0.9 }}
                  data-testid="bar-claimed-counts"
                >
                  {winnerCount} — {loserCount}
                </div>
              </div>
              <Icon name="trophy" size={40} />
            </div>
            <p className="body-sm muted" style={{ marginTop: 8 }}>
              {loser.name} needs <strong>{winnerCount + 1}+</strong> here to take it back.
            </p>
          </div>
        </div>

        <div className="row" style={{ gap: 10 }}>
          <button
            type="button"
            className="btn full"
            onClick={() => navigate({ to: '/squad/battle/$id', params: { id } })}
            style={{ minHeight: 56 }}
            data-testid="bar-claimed-back"
          >
            Back to battle
          </button>
        </div>
      </div>
    </div>
  )
}
